/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * "On a schedule" settings for the trigger (backend
 * Services/automation_schedule.py): how often, at what local time, and in
 * which time zone. Each run acts once on every record that matches the
 * automation's conditions.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import MenuItem from "@mui/material/MenuItem";
import LabeledField from "./LabeledField";
import Typography from "@mui/material/Typography";
import { v2 as t } from "styles/tokens/v2";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (error) {
    return "UTC";
  }
};

export const defaultSchedule = () => ({ frequency: "daily", time: "09:00", weekdays: [1, 2, 3, 4, 5], day_of_month: 1, timezone: browserTimeZone() });

/** "Every weekday at 09:00 (Asia/Kolkata)". */
export function describeSchedule(s) {
  if (!s) return "Choose when it runs";
  const tz = s.timezone || "UTC";
  if (s.frequency === "weekly") {
    const days = [...(s.weekdays || [])].sort();
    const head = days.join() === "1,2,3,4,5" ? "Every weekday" : days.length ? `Every ${days.map((d) => DAYS[d - 1]).join(", ")}` : "Pick the days";
    return `${head} at ${s.time} (${tz})`;
  }
  if (s.frequency === "monthly") return `Monthly on day ${s.day_of_month || 1} at ${s.time} (${tz})`;
  return `Every day at ${s.time} (${tz})`;
}

/** Only the keys the backend reads for this frequency. */
export function scheduleForSave(s) {
  const out = { frequency: s.frequency, time: s.time, timezone: s.timezone || "UTC" };
  if (s.frequency === "weekly") out.weekdays = [...(s.weekdays || [])].sort();
  if (s.frequency === "monthly") out.day_of_month = Number(s.day_of_month) || 1;
  return out;
}

export default function ScheduleEditor({ schedule, onChange, nextRunAt }) {
  const set = (patch) => onChange({ ...schedule, ...patch });
  const toggleDay = (d) => {
    const days = new Set(schedule.weekdays || []);
    if (days.has(d)) days.delete(d);
    else days.add(d);
    set({ weekdays: [...days].sort() });
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2.5, pt: 2.5, borderTop: `1px solid ${t.border}` }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>Schedule</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <LabeledField select label="Runs" value={schedule.frequency} onChange={(e) => set({ frequency: e.target.value })}>
          <MenuItem value="daily">Every day</MenuItem>
          <MenuItem value="weekly">On certain weekdays</MenuItem>
          <MenuItem value="monthly">Once a month</MenuItem>
        </LabeledField>
        <LabeledField type="time" label="At" value={schedule.time} onChange={(e) => set({ time: e.target.value || "09:00" })} />
      </Box>
      {schedule.frequency === "weekly" && (
        <Box role="group" aria-label="Weekdays" sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {DAYS.map((label, i) => {
            const on = (schedule.weekdays || []).includes(i + 1);
            return (
              <ButtonBase
                key={label}
                aria-pressed={on}
                onClick={() => toggleDay(i + 1)}
                sx={{ px: 1.25, height: 32, borderRadius: "8px", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, border: `1px solid ${on ? t.primary : t.border2}`, bgcolor: on ? "#E6EFFB" : t.surface, color: on ? "#1453A0" : t.text2 }}
              >
                {label}
              </ButtonBase>
            );
          })}
        </Box>
      )}
      {schedule.frequency === "monthly" && (
        <LabeledField
          type="number"
          label="Day of the month"
          value={schedule.day_of_month || 1}
          inputProps={{ min: 1, max: 31 }}
          helperText="In shorter months it runs on the last day."
          onChange={(e) => set({ day_of_month: Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
        />
      )}
      <LabeledField label="Time zone" value={schedule.timezone || ""} helperText="For example Asia/Kolkata or Australia/Sydney." onChange={(e) => set({ timezone: e.target.value.trim() })} />
      <Typography sx={{ fontSize: 13.5, color: t.text2 }}>
        {describeSchedule(schedule)}. Each run acts once on every record that matches the conditions, up to 500 at a time.
        {nextRunAt ? ` Next run ${new Date(nextRunAt).toLocaleString()}.` : ""}
      </Typography>
    </Box>
  );
}

ScheduleEditor.propTypes = {
  schedule: PropTypes.shape({
    frequency: PropTypes.string,
    time: PropTypes.string,
    weekdays: PropTypes.arrayOf(PropTypes.number),
    day_of_month: PropTypes.number,
    timezone: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  nextRunAt: PropTypes.string,
};
ScheduleEditor.defaultProps = { nextRunAt: null };
